import { useEffect, useRef, useCallback } from 'react';
import { useAppStore, usePomodoroStore } from '../stores/useAppStore';
import { useRestStore } from '../stores/useRestStore';
import { useAudioStore } from '../stores/useAudioStore';
import { getTodayDate } from '../utils/dateUtils';

// 心配ライン超過時の休息うながし選択肢。表示条件を満たしたらtrueを返す
// （options.forceで条件を無視して表示できる。デバッグボタンから使用）
export const showRestChoiceIfNeeded = (
  onShowMessage?: (message: string, duration?: number) => void,
  onShowChoice?: (scenario: any) => void,
  options?: { force?: boolean }
): boolean => {
  const { restSettings } = useRestStore.getState();
  const { pomodoroLog } = usePomodoroStore.getState();
  const today = getTodayDate();
  const todayMinutes = pomodoroLog[today] || 0;

  // 今日すでに「休む」を選んでいれば出さない
  const isRestingToday =
    restSettings.isRestMode && restSettings.lastRestDate === today;
  const shouldShow =
    todayMinutes >= restSettings.maxDailyMinutes &&
    restSettings.reminderLevel !== 'off' &&
    !isRestingToday;
  if (!shouldShow && !options?.force) return false;

  if (onShowChoice) {
    onShowChoice({
      question: '今日はもう' + Math.floor(todayMinutes / 60) + '時間' + (todayMinutes % 60) + '分も頑張ったね！どうする？',
      choices: [
        { id: 'rest', text: 'そうだね、少し休もうかな' },
        { id: 'continue', text: 'まだまだ頑張るよ！' },
        { id: 'later', text: 'あと少しだけ...' }
      ],
      onSelect: (choiceId: string) => {
        let message = '';
        switch(choiceId) {
          case 'rest':
            useRestStore.getState().setRestMode(true);
            useRestStore.getState().logRestDay();
            message = restSettings.reminderLevel === 'gentle' ?
              'えらい！休憩も大切だよ。ゆっくり休んでね' :
              'はい、休憩しましょう。お疲れ様でした';
            break;
          case 'continue':
            message = restSettings.reminderLevel === 'gentle' ?
              'すごい集中力！でも無理しないでね' :
              '体調に気をつけて作業してください';
            break;
          case 'later':
            message = restSettings.reminderLevel === 'gentle' ?
              'わかった！あと少しだけね。応援してる！' :
              'では、もう少しだけ。適度に休憩してください';
            break;
        }
        if (onShowMessage) {
          onShowMessage(message, 4000);
        }
      }
    });
  }
  return true;
};

export const usePomodoroTimer = (
  onShowMessage?: (message: string, duration?: number) => void,
  onShowChoice?: (scenario: any) => void
) => {
  const {
    currentSession,
    pomodoroSettings,
    pomodoroLog,
    startTimer,
    stopTimer,
    resetTimer,
    updateTimerRemaining,
  } = useAppStore();

  const intervalRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(0);
  const autoStartTimeoutRef = useRef<number | null>(null);

  // Format time for display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle start
  const handleStart = useCallback(() => {
    startTimer();
  }, [startTimer]);

  // Handle stop
  const handleStop = useCallback(() => {
    stopTimer();
  }, [stopTimer]);

  // Handle reset
  const handleReset = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoStartTimeoutRef.current) {
      window.clearTimeout(autoStartTimeoutRef.current);
      autoStartTimeoutRef.current = null;
    }
    resetTimer();
  }, [resetTimer]);

  // Timer effect - Vue版のタイマーロジックを再現
  useEffect(() => {
    if (currentSession.isRunning) {
      // Clear any existing interval first
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }

      // Store the current timestamp as baseline
      lastTickRef.current = Date.now();

      // Start new interval
      intervalRef.current = window.setInterval(() => {
        const { currentSession: session, pomodoroSettings: settings } = usePomodoroStore.getState();
        const now = Date.now();
        const diffSeconds = Math.floor((now - lastTickRef.current) / 1000);

        if (diffSeconds <= 0) {
          return;
        }

        // Advance baseline by elapsed whole seconds
        lastTickRef.current += diffSeconds * 1000;

        if (session.timerRemaining > 0) {
          const newRemaining = session.timerRemaining - diffSeconds;

          // Show messages at milestones (like Vue version)
          if (session.phase === 'work' && onShowMessage) {
            // 5 minutes remaining
            if (session.timerRemaining > 300 && newRemaining <= 300) {
              const runningMessages = [
                '20分も集中できてる！すごい！',
                'ここまで頑張れたのがえらい！',
                'もう20分も作業してる！素晴らしい！'
              ];
              const randomMessage = runningMessages[Math.floor(Math.random() * runningMessages.length)];
              onShowMessage(randomMessage, 3000);
            }
            // 1 minute remaining
            else if (session.timerRemaining > 60 && newRemaining <= 60) {
              const runningMessages = [
                'もうすぐ25分達成！完璧だよ！',
                'ほぼ完走！すごい集中力！',
                'あと少しで達成！素晴らしい！'
              ];
              const randomMessage = runningMessages[Math.floor(Math.random() * runningMessages.length)];
              onShowMessage(randomMessage, 3000);
            }
          }

          if (newRemaining > 0) {
            usePomodoroStore.getState().updateTimerRemaining(newRemaining);
            return;
          }
          // If newRemaining <= 0, fall through to session completion
          usePomodoroStore.getState().updateTimerRemaining(0);
        }

        // Session complete
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // Store the current phase before any changes
        const completedPhase = session.phase;

        // Complete current session
        usePomodoroStore.getState().completeSession();

        // 作業セッション完了時に休息設定をチェック（心配ライン超えたら選択肢を表示）
        if (completedPhase === 'work') {
          const restShown = showRestChoiceIfNeeded(onShowMessage, onShowChoice);

          // 目標達成メッセージ（休息うながしが出ず、今回のセッションで目標を跨いだ時のみ）
          if (!restShown) {
            const { restSettings } = useRestStore.getState();
            const { pomodoroLog } = usePomodoroStore.getState();
            const todayMinutes = pomodoroLog[getTodayDate()] || 0;
            if (todayMinutes >= restSettings.dailyGoalMinutes && todayMinutes - settings.workDuration < restSettings.dailyGoalMinutes) {
              const messages = [
                '目標達成！素晴らしい！',
                '今日の目標クリア！えらい！',
                '目標時間に到達！完璧だよ！'
              ];
              const randomMessage = messages[Math.floor(Math.random() * messages.length)];
              onShowMessage?.(randomMessage, 4000);
            }
          }
        }

        // Switch phase (work -> break or break -> work)
        usePomodoroStore.getState().switchPhase();
        
        // Auto-start next session if enabled
        // Note: Check the COMPLETED phase, not the new phase
        const shouldAutoStart = (completedPhase === 'work' && settings.autoStartBreaks) ||
                                (completedPhase === 'break' && settings.autoStartPomodoros);

        if (shouldAutoStart) {
          // Small delay before auto-starting
          if (autoStartTimeoutRef.current) {
            window.clearTimeout(autoStartTimeoutRef.current);
          }
          autoStartTimeoutRef.current = window.setTimeout(() => {
            autoStartTimeoutRef.current = null;
            usePomodoroStore.getState().startTimer();
          }, 1000);
        }

        // Play completion voice (Vue版準拠: workEnd/breakEndからランダム)
        if (settings.soundEnabled) {
          const voices = completedPhase === 'work'
            ? ['workEnd.mp3', 'workEnd2.mp3', 'workEnd3.mp3']
            : ['breakEnd.mp3', 'breakEnd2.mp3'];
          const voice = voices[Math.floor(Math.random() * voices.length)];
          const { audioSettings } = useAudioStore.getState();
          const audio = new Audio(`/assets/audio/se/${voice}`);
          audio.volume = audioSettings.seVolume * audioSettings.masterVolume;
          audio.play().catch(() => {});
        }

        // Show notification
        if (settings.notificationEnabled && 'Notification' in window && Notification.permission === 'granted') {
          const sessionType = session.phase === 'work' ? '作業' : '休憩';
          new Notification('Sorarium', {
            body: `${sessionType}セッションが完了しました！`,
            icon: '/icon-192x192.png',
          });
        }

      }, 250);
    } else {
      // Stop interval when not running
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    currentSession.isRunning,
    // 他の依存は実際には不要 - タイマーの開始/停止のみを監視
  ]);

  // Check for notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    // アンマウント時に自動開始の予約を破棄
    return () => {
      if (autoStartTimeoutRef.current) {
        window.clearTimeout(autoStartTimeoutRef.current);
        autoStartTimeoutRef.current = null;
      }
    };
  }, []);

  // デバッグ用: セッションを即座に完了させる
  const debugCompleteSession = useCallback(() => {
    if (currentSession.isRunning && currentSession.phase) {
      // タイマーを0にして完了処理をトリガー
      updateTimerRemaining(0);
    }
  }, [currentSession.isRunning, currentSession.phase, updateTimerRemaining]);

  // 今日の作業時間を計算
  const today = getTodayDate();
  const todayWorkMinutes = pomodoroLog[today] || 0;

  return {
    // State
    timerRemaining: currentSession.timerRemaining,
    formattedTime: formatTime(currentSession.timerRemaining),
    isRunning: currentSession.isRunning,
    phase: currentSession.phase,
    completedSessions: currentSession.completedSessions,
    todayWorkMinutes,
    
    // Actions
    handleStart,
    handleStop,
    handleReset,
    debugCompleteSession,
    
    // Settings
    workDuration: pomodoroSettings.workDuration,
    breakDuration: pomodoroSettings.breakDuration,
  };
};